/**
 * Global rate-limit pause signal.
 *
 * When any API call receives a 429, all polling pauses for the Retry-After
 * duration. Centralized here so polling hooks don't need to know about
 * rate limits — they just get a longer interval from getEffectivePollingInterval().
 */

export class RateLimitedError extends Error {
  public retryAfterMs: number
  constructor(retryAfterMs: number) {
    super('Rate limited')
    this.name = 'RateLimitedError'
    this.retryAfterMs = retryAfterMs
  }
}

let rateLimitedUntil = 0

/** How long after a rate limit expires we still treat network errors as 429s */
const RATE_LIMIT_GRACE_MS = 5_000

/**
 * Progressive backoff for TypeError gates (CORS-blocked 429s).
 *
 * When a fetch() throws TypeError (CORS-blocked 429), we set a short 5s gate
 * since we can't distinguish it from a genuine network error. If another
 * TypeError fires right after the gate opens, we double the gate each time,
 * up to 60s. This prevents the 5s-cycle pattern where the gate opens,
 * a few requests fail, gate closes, repeat.
 *
 * Reset to initial when a proper 429 or successful request comes through.
 */
const INITIAL_TYPEERROR_GATE_MS = 5_000
const MAX_TYPEERROR_GATE_MS = 60_000
let currentTypeErrorGate = INITIAL_TYPEERROR_GATE_MS

export function markRateLimited(retryAfterMs = 60_000) {
  rateLimitedUntil = Date.now() + retryAfterMs
}

/**
 * Mark rate-limited due to a TypeError (CORS-blocked response).
 * Uses progressive backoff: 5s → 10s → 20s → 40s → 60s.
 * Returns the gate duration that was applied.
 */
export function markTypeErrorRateLimited(): number {
  const gate = currentTypeErrorGate
  markRateLimited(gate)
  // Escalate for next time (double, capped at 60s)
  currentTypeErrorGate = Math.min(currentTypeErrorGate * 2, MAX_TYPEERROR_GATE_MS)
  return gate
}

/**
 * Reset TypeError backoff to initial value.
 * Call when a proper response (200 or readable 429) comes through,
 * proving the CORS-blocked phase has ended.
 */
export function resetTypeErrorBackoff() {
  currentTypeErrorGate = INITIAL_TYPEERROR_GATE_MS
}

export function isRateLimited(): boolean {
  return Date.now() < rateLimitedUntil
}

/**
 * Returns true if we are rate-limited OR were very recently.
 * Used to attribute CORS-blocked network errors to rate limiting —
 * when the server omits CORS headers on 429 responses, fetch() throws
 * a TypeError instead of returning a Response, so handleResponse()
 * (which calls markRateLimited) is never reached.
 */
export function wasRecentlyRateLimited(): boolean {
  return Date.now() < rateLimitedUntil + RATE_LIMIT_GRACE_MS
}

export function getRateLimitDelay(): number {
  return Math.max(0, rateLimitedUntil - Date.now())
}
